import {
  domToReact,
  htmlToDOM,
  type DOMNode,
  type HTMLReactParserOptions,
} from 'html-react-parser';

// html-react-parser renders whatever it is given: <script> bodies, srcdoc,
// javascript: URLs and on* attributes all reach React. This allowlist runs on
// the parsed tree before domToReact; landing has the same helper for its SSR pages.

// Removed together with their contents.
const DROP_TAGS = new Set([
  'script',
  'style',
  'iframe',
  'frame',
  'frameset',
  'object',
  'embed',
  'applet',
  'noscript',
  'noembed',
  'template',
  'textarea',
  'select',
  'option',
  'button',
  'input',
  'form',
  'title',
  'head',
  'meta',
  'link',
  'base',
  'xml',
  'foreignobject',
  'annotation-xml',
  'portal',
]);

// Rendered as-is. Anything else (custom elements included) is unwrapped to its children.
const ALLOWED_TAGS = new Set([
  'a',
  'abbr',
  'b',
  'bdi',
  'bdo',
  'blockquote',
  'br',
  'caption',
  'cite',
  'code',
  'col',
  'colgroup',
  'dd',
  'del',
  'details',
  'dfn',
  'div',
  'dl',
  'dt',
  'em',
  'figcaption',
  'figure',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'hr',
  'i',
  'img',
  'ins',
  'kbd',
  'li',
  'mark',
  'ol',
  'p',
  'pre',
  'q',
  's',
  'samp',
  'small',
  'span',
  'strike',
  'strong',
  'sub',
  'summary',
  'sup',
  'table',
  'tbody',
  'td',
  'tfoot',
  'th',
  'thead',
  'tr',
  'u',
  'ul',
  'var',
  'wbr',
  // MathML (question/option content)
  'math',
  'semantics',
  'annotation',
  'mi',
  'mo',
  'mn',
  'mrow',
  'msub',
  'msup',
  'msubsup',
  'mfrac',
  'msqrt',
  'mroot',
  'mover',
  'munder',
  'munderover',
  'mtable',
  'mtr',
  'mtd',
  'mtext',
  'mspace',
  'mstyle',
  'mpadded',
  'mphantom',
  'menclose',
  'mfenced',
  'mmultiscripts',
  'mprescripts',
  'none',
  'ms',
  // SVG glyphs emitted by KaTeX
  'svg',
  'path',
  'line',
  'g',
  'rect',
  'circle',
  'polyline',
  'polygon',
]);

const ALLOWED_ATTRS = new Set([
  'class',
  'id',
  'style',
  'title',
  'alt',
  'src',
  'href',
  'target',
  'rel',
  'width',
  'height',
  'align',
  'valign',
  'colspan',
  'rowspan',
  'scope',
  'start',
  'type',
  'reversed',
  'value',
  'dir',
  'lang',
  'role',
  'border',
  'cellpadding',
  'cellspacing',
  'open',
  'loading',
  'datetime',
  'cite',
  // MathML
  'mathvariant',
  'display',
  'displaystyle',
  'xmlns',
  'encoding',
  'stretchy',
  'fence',
  'separator',
  'separators',
  'accent',
  'accentunder',
  'lspace',
  'rspace',
  'linethickness',
  'close',
  'columnalign',
  'rowalign',
  'columnspacing',
  'rowspacing',
  'notation',
  'depth',
  'scriptlevel',
  'mathsize',
  'mathcolor',
  'mathbackground',
  'movablelimits',
  'largeop',
  'symmetric',
  'minsize',
  'maxsize',
  'form',
  'voffset',
  // SVG
  'viewbox',
  'd',
  'fill',
  'stroke',
  'stroke-width',
  'preserveaspectratio',
  'x',
  'y',
  'x1',
  'y1',
  'x2',
  'y2',
  'cx',
  'cy',
  'r',
  'points',
  'transform',
]);

const URL_ATTRS = new Set(['href', 'src', 'cite']);

// Browsers ignore control characters and whitespace inside a scheme ("java\tscript:").
const stripUrlNoise = (url: string) =>
  // eslint-disable-next-line no-control-regex
  url.replace(/[\u0000- \u007f-\u009f]/g, '').toLowerCase();

const isSafeUrl = (url: string, attr: string) => {
  const cleaned = stripUrlNoise(url);
  if (/^(https?:|mailto:|tel:)/.test(cleaned)) return true;
  if (attr === 'src' && cleaned.startsWith('data:image/')) return true;
  // Relative, root-relative, fragment and query URLs carry no scheme.
  const firstSeparator = cleaned.search(/[/?#]/);
  const colon = cleaned.indexOf(':');
  return colon === -1 || (firstSeparator !== -1 && firstSeparator < colon);
};

const isAllowedAttr = (name: string) =>
  ALLOWED_ATTRS.has(name) ||
  name.startsWith('data-') ||
  name.startsWith('aria-');

const sanitizeAttribs = (attribs: Record<string, string>) => {
  const clean: Record<string, string> = {};
  Object.keys(attribs).forEach((key) => {
    const name = key.toLowerCase();
    if (!isAllowedAttr(name)) return;
    if (URL_ATTRS.has(name) && !isSafeUrl(attribs[key], name)) return;
    clean[key] = attribs[key];
  });
  return clean;
};

// The tree is freshly parsed and owned here, so it is relinked in place.
/* eslint-disable no-param-reassign */
const sanitizeNodes = (nodes: DOMNode[], parent: any): DOMNode[] => {
  const out: DOMNode[] = [];
  nodes.forEach((node: any) => {
    if (node.type === 'text') {
      node.parent = parent;
      out.push(node);
      return;
    }
    // script/style parse to their own node types; comments, directives and CDATA go too.
    if (node.type !== 'tag') return;
    const name = String(node.name).toLowerCase();
    if (DROP_TAGS.has(name)) return;
    const children = sanitizeNodes(node.children || [], node);
    if (!ALLOWED_TAGS.has(name)) {
      children.forEach((child: any) => {
        child.parent = parent;
        out.push(child);
      });
      return;
    }
    node.attribs = sanitizeAttribs(node.attribs || {});
    node.children = children;
    node.parent = parent;
    out.push(node);
  });
  out.forEach((node: any, i) => {
    node.prev = out[i - 1] ?? null;
    node.next = out[i + 1] ?? null;
  });
  return out;
};
/* eslint-enable no-param-reassign */

/** Drop-in replacement for html-react-parser's default export that sanitizes first. */
const parseHtml = (html: string, options?: HTMLReactParserOptions) => {
  if (typeof html !== 'string') {
    throw new TypeError('First argument must be a string');
  }
  if (!html) return [];
  const dom = htmlToDOM(html, {
    ...(options?.htmlparser2 ?? { lowerCaseAttributeNames: false }),
    trustedTypePolicy: options?.trustedTypePolicy,
  });
  return domToReact(sanitizeNodes(dom, null), options);
};

export default parseHtml;
